import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { WEB_APP_URL } from '../../utils/webApp';
import './GetAppModal.css';

const APP_STORE_URL = 'https://apps.apple.com/app/id6747190886';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.voia.travel.mobile';

/**
 * "Get the app" for desktop visitors: a QR code to scan with the phone (it opens
 * /get, which sends each phone to its own store), the store badges for anyone
 * who'd rather send the link themselves, and the web app as the thing they can
 * use right now on this screen.
 *
 * Rendered into document.body: the transparent navbar is positioned with a
 * transform, which would otherwise pin this fixed overlay to the navbar.
 */
const GetAppModal = ({ open, onClose }) => {
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="getAppBackdrop" onClick={onClose}>
      <div
        className="getAppModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="getAppTitle"
        data-track-location="get_app_modal"
        onClick={(event) => event.stopPropagation()}
      >
        <button ref={closeRef} type="button" className="getAppClose" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <h2 id="getAppTitle" className="getAppTitle">Get VOIA on your phone</h2>
        <p className="getAppText">Scan with your phone's camera to download the app.</p>
        <img src="/assets/get-app-qr.svg" alt="QR code to download VOIA" className="getAppQr" width="200" height="200" />
        <div className="getAppStores">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/assets/app_store_badge.svg" alt="Download VOIA on the App Store" />
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
            <img src="/assets/google_play_badge.png" alt="Get VOIA on Google Play" />
          </a>
        </div>
        <p className="getAppWeb">
          On a laptop? <a href={WEB_APP_URL}>Plan your trips on the web</a>
        </p>
      </div>
    </div>,
    document.body,
  );
};

export default GetAppModal;
